import React, { useState, useCallback } from "react";
import {
    PrimaryButton,
    DefaultButton,
    Stack,
    TextField,
    Spinner,
    Label,
    MessageBar,
    MessageBarType,
    IconButton,
} from "@fluentui/react";
import {
    createDecorationBatch,
    BatchDecorationImage,
} from "src/services/aigc/workflowOnline";
import style from "./index.module.scss";

// Predefined color options for easy selection
const colorOptions = [
    { hex: "#C41E3A", name: "Crimson Red" },
    { hex: "#1E3A5F", name: "Navy Blue" },
    { hex: "#2E7D32", name: "Forest Green" },
    { hex: "#E65100", name: "Deep Orange" },
    { hex: "#6A1B9A", name: "Purple" },
    { hex: "#F9A825", name: "Amber Yellow" },
    { hex: "#00695C", name: "Teal" },
    { hex: "#5D4037", name: "Brown" },
    { hex: "#C2185B", name: "Pink" },
    { hex: "#0078D4", name: "Microsoft Blue" },
];

export const IconCreate: React.FC = () => {
    const [contents, setContents] = useState<string[]>([""]);
    const [mainColor, setMainColor] = useState("#C41E3A");
    const [customColor, setCustomColor] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [generatedImages, setGeneratedImages] = useState<BatchDecorationImage[]>([]);
    const [zipBlob, setZipBlob] = useState<Blob | null>(null);
    const [manifest, setManifest] = useState<{ total: number; successful: number; failed: number } | null>(null);

    const handleContentChange = useCallback((index: number, value: string) => {
        setContents((prev) => {
            const newContents = [...prev];
            newContents[index] = value;
            return newContents;
        });
    }, []);

    const handleAddContent = useCallback(() => {
        setContents((prev) => [...prev, ""]);
    }, []);

    const handleRemoveContent = useCallback((index: number) => {
        setContents((prev) => {
            if (prev.length <= 1) return prev;
            return prev.filter((_, i) => i !== index);
        });
    }, []);

    const handleCreate = async () => {
        const validContents = contents.filter((c) => c.trim().length > 0);
        if (validContents.length === 0) {
            setError("Please enter at least one content description.");
            return;
        }

        const colorToUse = customColor.match(/^#[0-9A-Fa-f]{6}$/)
            ? customColor
            : mainColor;

        setError(null);
        setIsLoading(true);
        setGeneratedImages([]);
        setZipBlob(null);
        setManifest(null);

        try {
            const result = await createDecorationBatch({
                contents: validContents,
                mainColor: colorToUse,
            });

            setGeneratedImages(result.images);
            setZipBlob(result.zipBlob);
            setManifest(result.manifest);
        } catch (err: unknown) {
            const axiosError = err as {
                response?: { data?: { message?: string } };
                message?: string;
            };
            const message = axiosError?.response?.data?.message
                || axiosError?.message
                || String(err);
            setError(`Failed to create icons: ${message}`);
        } finally {
            setIsLoading(false);
        }
    };

    const handleClear = () => {
        setContents([""]);
        setMainColor("#C41E3A");
        setCustomColor("");
        setGeneratedImages([]);
        setZipBlob(null);
        setManifest(null);
        setError(null);
    };

    const handleDownloadZip = () => {
        if (!zipBlob) return;

        const url = URL.createObjectURL(zipBlob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "decorations-batch.zip";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    const handleDownloadSingle = (image: BatchDecorationImage) => {
        if (!image.blob || !image.objectUrl) return;

        const a = document.createElement("a");
        a.href = image.objectUrl;
        a.download = `decoration-${image.index}.png`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    };

    const isValidCustomColor = customColor.match(/^#[0-9A-Fa-f]{6}$/);
    const selectedColor = isValidCustomColor ? customColor : mainColor;
    const validContentCount = contents.filter((c) => c.trim().length > 0).length;

    return (
        <div className={style.iconCreatePage}>
            <h1>Batch Icon Create</h1>
            <p className={style.description}>
                Generate multiple decorative PNG icons with unified visual style.
                All icons will share the same design language for consistency.
            </p>

            <Stack tokens={{ childrenGap: 16 }} className={style.container}>
                {/* Content Inputs */}
                <div className={style.inputSection}>
                    <Label required>Content Descriptions</Label>
                    <p className={style.hint}>
                        Add multiple content items to generate icons with consistent style.
                    </p>
                    {contents.map((content, index) => (
                        <Stack
                            horizontal
                            key={`content-${String(index)}`}
                            tokens={{ childrenGap: 8 }}
                            verticalAlign="start"
                            className={style.contentRow}
                        >
                            <TextField
                                multiline
                                rows={2}
                                placeholder={`Content ${index + 1}: Enter description`}
                                value={content}
                                onChange={(_, v) => handleContentChange(index, v || "")}
                                styles={{ root: { flex: 1 } }}
                            />
                            {contents.length > 1 && (
                                <IconButton
                                    iconProps={{ iconName: "Delete" }}
                                    title="Remove this content"
                                    onClick={() => handleRemoveContent(index)}
                                    className={style.deleteButton}
                                />
                            )}
                        </Stack>
                    ))}
                    <DefaultButton
                        text="+ Add Another Content"
                        onClick={handleAddContent}
                        iconProps={{ iconName: "Add" }}
                        className={style.addButton}
                    />
                </div>

                {/* Color Selection */}
                <div className={style.inputSection}>
                    <Label required>Main Color (applies to all icons)</Label>
                    <div className={style.colorGrid}>
                        {colorOptions.map((color) => (
                            <div
                                key={color.hex}
                                className={`${style.colorOption} ${
                                    mainColor === color.hex && !customColor
                                        ? style.colorSelected
                                        : ""
                                }`}
                                style={{ backgroundColor: color.hex }}
                                onClick={() => {
                                    setMainColor(color.hex);
                                    setCustomColor("");
                                }}
                                title={color.name}
                                aria-label={`Select ${color.name} color`}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                        setMainColor(color.hex);
                                        setCustomColor("");
                                    }
                                }}
                            />
                        ))}
                    </div>
                    <Stack
                        horizontal
                        tokens={{ childrenGap: 12 }}
                        verticalAlign="end"
                        className={style.customColorRow}
                    >
                        <TextField
                            label="Custom Color (Hex)"
                            placeholder="#RRGGBB"
                            value={customColor}
                            onChange={(_, v) => setCustomColor(v || "")}
                            styles={{ root: { width: 150 } }}
                        />
                        {customColor && (
                            <div
                                className={style.customColorPreview}
                                style={{
                                    backgroundColor: isValidCustomColor
                                        ? customColor
                                        : "#ccc",
                                }}
                            />
                        )}
                    </Stack>
                    <div className={style.selectedColorInfo}>
                        {`Selected: ${selectedColor}`}
                    </div>
                </div>

                {/* Action Buttons */}
                <Stack horizontal tokens={{ childrenGap: 12 }}>
                    <PrimaryButton
                        text={`Create ${validContentCount} Icon${validContentCount !== 1 ? "s" : ""}`}
                        onClick={handleCreate}
                        disabled={isLoading || validContentCount === 0}
                    />
                    <DefaultButton
                        text="Clear All"
                        onClick={handleClear}
                        disabled={isLoading}
                    />
                </Stack>

                {/* Loading State */}
                {isLoading && (
                    <div className={style.loadingSection}>
                        <Spinner label={`Generating ${validContentCount} icon${validContentCount !== 1 ? "s" : ""} with unified style... This may take a few minutes.`} />
                    </div>
                )}

                {/* Error Message */}
                {error && (
                    <MessageBar
                        messageBarType={MessageBarType.error}
                        onDismiss={() => setError(null)}
                    >
                        {error}
                    </MessageBar>
                )}

                {/* Quick ZIP Download - shown immediately when available */}
                {zipBlob && !isLoading && (
                    <MessageBar
                        messageBarType={MessageBarType.success}
                        isMultiline={false}
                        actions={(
                            <div>
                                <PrimaryButton
                                    text="Download ZIP"
                                    onClick={handleDownloadZip}
                                    iconProps={{ iconName: "ZipFolder" }}
                                />
                            </div>
                        )}
                    >
                        {manifest
                            ? `Generation complete! ${manifest.successful}/${manifest.total} icons generated successfully.`
                            : "Generation complete! ZIP file is ready for download."}
                    </MessageBar>
                )}

                {/* Result Display */}
                {generatedImages.length > 0 && (
                    <div className={style.resultSection}>
                        <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
                            <Label>Generated Icons</Label>
                            {manifest && (
                                <span className={style.manifestInfo}>
                                    {`${manifest.successful}/${manifest.total} successful`}
                                </span>
                            )}
                        </Stack>

                        <div className={style.imageGrid}>
                            {generatedImages.map((image) => (
                                <div
                                    key={`image-${image.index}`}
                                    className={`${style.imageCard} ${image.status === "failed" ? style.imageFailed : ""}`}
                                >
                                    <div className={style.imagePreview}>
                                        {image.objectUrl ? (
                                            <img
                                                src={image.objectUrl}
                                                alt={`Icon ${image.index}`}
                                                className={style.generatedImage}
                                            />
                                        ) : (
                                            <div className={style.imagePlaceholder}>
                                                Failed
                                            </div>
                                        )}
                                    </div>
                                    <div className={style.imageInfo}>
                                        <span className={style.imageIndex}>{`#${image.index}`}</span>
                                        <span className={style.imageSubject} title={image.subject}>
                                            {image.subject}
                                        </span>
                                    </div>
                                    {image.blob && (
                                        <DefaultButton
                                            text="Download"
                                            onClick={() => handleDownloadSingle(image)}
                                            iconProps={{ iconName: "Download" }}
                                            className={style.downloadButton}
                                        />
                                    )}
                                </div>
                            ))}
                        </div>

                        <Stack
                            horizontal
                            tokens={{ childrenGap: 12 }}
                            className={style.resultActions}
                        >
                            <PrimaryButton
                                text="Download All (ZIP)"
                                onClick={handleDownloadZip}
                                iconProps={{ iconName: "ZipFolder" }}
                                disabled={!zipBlob}
                            />
                        </Stack>
                    </div>
                )}
            </Stack>
        </div>
    );
};

export default IconCreate;
